import React, { useState } from 'react';
import {
  LayoutDashboard,
  Users,
  Beaker,
  Building2,
  Settings,
  LogOut,
  X,
  CheckCircle2,
  AlertCircle,
  Info,
  Zap,
} from 'lucide-react';
import { ScoutLoopMark } from '../components/brand/ScoutLoopMark';
import { useUI } from '../hooks/useUI';
import { useProfile } from '../hooks/useProfile';
import { useAuth } from '../hooks/useAuth';
import { SubscriptionModal } from '../components/SubscriptionModal';
import { QRManager } from '../components/messaging/QRManager';
import { TABS, AppTab } from '../types';

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { activeTab, setActiveTab, toasts, removeToast, showQR, setShowQR } = useUI();
  const { isCoach, isAD, isSysAdmin, isStaff } = useProfile();
  const { signOut, profile } = useAuth();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const subscriptionStatus = profile?.subscription_status;
  const isTrialUser = subscriptionStatus === 'trial';
  const isExpiredUser = subscriptionStatus === 'expired';

  const navItems: { id: AppTab; renderIcon: () => React.ReactNode; mobileOnly?: boolean }[] = [
    { id: TABS.HUB, renderIcon: () => <LayoutDashboard size={24} /> },
    ...(isCoach ? [{ id: TABS.TEAM, renderIcon: () => <Users size={24} /> }] : []),
    ...(isAD ? [{ id: TABS.DEPT, renderIcon: () => <Building2 size={24} /> }] : []),
    ...(!isStaff ? [{ id: TABS.LOCKER, renderIcon: () => <img src="/brand/icons/icon-vault.svg" alt="The Vault" width={24} height={24} /> }] : []),
    ...(!isStaff ? [{ id: TABS.CRM, renderIcon: () => <Users size={24} /> }] : []),
    { id: TABS.INBOX, renderIcon: () => <img src="/brand/icons/icon-messenger.svg" alt="Magic Messenger" width={24} height={24} /> },
    ...(isSysAdmin ? [{ id: TABS.LAB, renderIcon: () => <Beaker size={24} /> }] : []),
    // Mobile-only — desktop already has Settings in the sidebar bottom section
    { id: TABS.SETTINGS, renderIcon: () => <Settings size={24} />, mobileOnly: true },
  ];

  return (
    <div className="app-shell bg-slate-50 flex flex-col font-['Inter'] relative">
      {/* Trial / Expired upgrade banner */}
      {(isTrialUser || isExpiredUser) && (
        <div
          className={`flex items-center justify-between px-4 py-2.5 text-white text-[10px] font-black uppercase tracking-widest ${isExpiredUser ? 'bg-red-600' : 'bg-amber-600'}`}
        >
          <div className="flex items-center gap-2">
            <Zap size={14} />
            {isExpiredUser
              ? 'Your free trial has ended. Upgrade to keep access.'
              : 'You are on a free trial. Upgrade anytime to go Pro.'}
          </div>
          <button
            onClick={() => setShowSubscriptionModal(true)}
            className="ml-4 shrink-0 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition"
          >
            Upgrade
          </button>
        </div>
      )}

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      {showQR && profile && (
        <QRManager
          mode={isCoach ? 'scan' : 'show'}
          profile={profile}
          onClose={() => setShowQR(false)}
        />
      )}

      {/* GLOBAL TOAST LAYER */}
      <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[600] flex flex-col gap-2 w-full max-w-sm px-4 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto bg-slate-900 text-white p-4 rounded-2xl shadow-2xl flex items-center justify-between gap-4 border border-white/10 animate-in slide-in-from-top-4 duration-300"
          >
            <div className="flex items-center gap-3">
              {toast.type === 'success' && <CheckCircle2 size={18} className="text-emerald-400" />}
              {toast.type === 'error' && <AlertCircle size={18} className="text-red-400" />}
              {toast.type === 'info' && <Info size={18} className="text-amber-400" />}
              <p className="text-[10px] font-black uppercase tracking-widest">{toast.message}</p>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-white/20 hover:text-white transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-1 flex-col lg:flex-row overflow-hidden">
        <nav className="fixed bottom-0 left-0 right-0 h-20 lg:static lg:w-80 lg:h-full lg:border-r bg-neutral-950 border-neutral-800 flex lg:flex-col items-center lg:items-stretch px-4 lg:py-8 justify-around lg:justify-start gap-4 z-[110] pb-safe border-t">
          <div className="hidden lg:flex flex-col gap-6 px-4 mb-8">
            <div className="flex items-center gap-3">
              <ScoutLoopMark size={36} color="#d97706" />
              <h2 className="text-xl font-black italic uppercase text-white leading-none">
                Scout<span className="text-amber-600">Loop</span>
              </h2>
            </div>
          </div>

          {navItems.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 p-4 rounded-2xl transition-all active:scale-95 ${tab.mobileOnly ? 'lg:hidden' : ''} ${activeTab === tab.id ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/20' : 'text-amber-600 hover:text-amber-700'}`}
            >
              {tab.renderIcon()}
              <span className="hidden lg:inline font-black uppercase text-[10px] tracking-[0.2em]">
                {tab.id}
              </span>
            </button>
          ))}

          <div className="hidden lg:block mt-auto space-y-2">
            <button
              onClick={() => setActiveTab(TABS.SETTINGS)}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl transition-all font-black uppercase text-[10px] tracking-[0.2em] ${activeTab === TABS.SETTINGS ? 'bg-slate-900 text-white' : 'text-amber-600 hover:text-amber-700'}`}
            >
              <Settings size={22} /> Settings
            </button>
            <button
              onClick={signOut}
              className="w-full flex items-center gap-3 p-4 rounded-2xl text-red-400 hover:bg-red-500/10 transition-all font-black uppercase text-[10px] tracking-[0.3em]"
            >
              <LogOut size={22} /> Exit Node
            </button>
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto overflow-x-hidden pb-24 lg:pb-8 pt-safe">
          <div className="max-w-7xl mx-auto p-4 lg:p-12">{children}</div>
        </main>
      </div>
    </div>
  );
};
